import { create } from 'zustand'
import type { Team, TeamBranding, TeamBrandingAsset } from '@/types'

interface TeamState {
  team: Team | null
  allTeams: Team[]
  branding: TeamBranding | null
  brandingAsset: TeamBrandingAsset | null
  isRealEstate: boolean
  hasClipArchiveAccess: boolean
  hasPushAccess: boolean
  availableVideoCredits: number | null
  availableInstantCredits: number | null
  availableProCredits: number | null
  storageUsedGB: number
  storageLimitGB: number

  setTeam: (team: Team | null) => void
  setAllTeams: (teams: Team[]) => void
  setBranding: (branding: TeamBranding | null) => void
  setBrandingAsset: (asset: TeamBrandingAsset | null) => void
  setCredits: (credits: { video?: number | null; instant?: number | null; pro?: number | null }) => void
  setStorage: (used: number, limit: number) => void
  reset: () => void
}

const initialState = {
  team: null,
  allTeams: [],
  branding: null,
  brandingAsset: null,
  isRealEstate: false,
  hasClipArchiveAccess: true,
  hasPushAccess: false,
  availableVideoCredits: null,
  availableInstantCredits: null,
  availableProCredits: null,
  storageUsedGB: 0,
  storageLimitGB: 0,
}

export const useTeamStore = create<TeamState>((set) => ({
  ...initialState,

  setTeam: (team) =>
    set({
      team,
      isRealEstate: team ? !team.tags?.some((t) => t.toLowerCase() === 'brand') : false,
      hasPushAccess: !!team?.customPushNotificationAccess,
    }),

  setAllTeams: (teams) => set({ allTeams: teams }),

  setBranding: (branding) => set({ branding }),

  setBrandingAsset: (asset) => set({ brandingAsset: asset }),

  setCredits: (credits) =>
    set((state) => ({
      availableVideoCredits: credits.video ?? state.availableVideoCredits,
      availableInstantCredits: credits.instant ?? state.availableInstantCredits,
      availableProCredits: credits.pro ?? state.availableProCredits,
    })),

  setStorage: (used, limit) => set({ storageUsedGB: used, storageLimitGB: limit }),

  reset: () => set(initialState),
}))
